/**
 * ReaderCreditsModal - Transaction Modal for Reader Credit Activity
 * 
 * Displays:
 * - Current authoritative balance
 * - Last 10 ledger transactions (newest first)
 * - Loading, error, and empty states
 * 
 * Purpose: Verification tool to confirm:
 * - Transactions are occurring during real playback
 * - Deltas are correct
 * - Running balance arithmetic is correct
 */
import React, { useEffect } from 'react';
import { useReaderCredits } from '../contexts/ReaderCreditContext';
import { X, RefreshCw, AlertCircle, Coins } from 'lucide-react';

export default function ReaderCreditsModal({ isOpen, onClose }) {
  const { 
    balance, 
    loading, 
    error, 
    transactions, 
    refreshCreditsSummary 
  } = useReaderCredits();

  // Refresh on open
  useEffect(() => {
    if (isOpen) {
      refreshCreditsSummary(10);
    }
  }, [isOpen, refreshCreditsSummary]);

  if (!isOpen) return null;

  // Format timestamp for display
  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      const date = new Date(ts);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  // Format delta with sign and color
  const formatDelta = (delta, type) => {
    if (typeof delta !== 'number') return '—';
    const isCredit = type === 'credit' || delta > 0;
    const sign = isCredit ? '+' : '';
    const colorClass = isCredit ? 'text-green-400' : 'text-red-400';
    return (
      <span className={colorClass}>
        {sign}{delta.toFixed(2)}
      </span>
    );
  };

  // Get human-readable type label
  const getTypeLabel = (tx) => {
    const type = tx.type || tx.reason || 'unknown';
    switch (type) {
      case 'Ask Audia': return 'Ask Audia';
      case 'Playback': return 'Playback';
      case 'usage_debit': return 'Playback';
      case 'interaction_debit': return 'Playback';
      case 'introductory_allocation': return 'Welcome Bonus';
      case 'admin_grant': return 'Admin Grant';
      case 'purchase': return 'Purchase';
      case 'audia_question': return 'Ask Audia';
      case 'reversal': return 'Reversal';
      case 'credit': return 'Credit';
      default: return type;
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-gray-900 border border-cyan-500/30 rounded-xl shadow-2xl w-[95vw] max-w-2xl mx-auto max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20">
          <div className="flex items-center gap-3">
            <Coins className="w-6 h-6 text-yellow-400" />
            <h2 className="text-xl font-bold text-white">Reader Credit Activity</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Balance Display */}
        <div className="px-6 py-4 bg-gray-800/50 border-b border-cyan-500/20">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-400">Current Balance</div>
              <div className="text-3xl font-bold text-yellow-400">
                {balance === null ? '—' : balance.toFixed(2)}
                <span className="text-lg text-gray-400 ml-2">Credits</span>
              </div>
            </div>
            <button
              onClick={() => refreshCreditsSummary(10)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 transition-colors text-white text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin" />
              <span className="ml-3 text-gray-400">Loading recent transactions…</span>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="flex flex-col items-center py-8">
              <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
              <div className="text-red-400 text-center mb-4">
                Failed to load transactions: {typeof error === 'string' ? error : error?.message || 'Unknown error'}
              </div>
              <button
                onClick={() => refreshCreditsSummary(10)}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium"
              >
                Retry
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && transactions.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              No transactions found.
            </div>
          )}

          {/* Transaction Table */}
          {!loading && !error && transactions.length > 0 && (
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-400 border-b border-gray-700">
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium text-right">Delta</th>
                  <th className="pb-2 font-medium text-center">Balance After</th>
                  <th className="pb-2 font-medium">Reference</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr 
                    key={tx.correlation_id || tx.ledger_id || idx} 
                    className="border-b border-gray-800 hover:bg-gray-800/50"
                  >
                    <td className="py-3 text-sm text-gray-300">
                      {formatTimestamp(tx.ts || tx.created_at)}
                    </td>
                    <td className="py-3 text-sm text-gray-300">
                      {getTypeLabel(tx)}
                    </td>
                    <td className="py-3 text-sm text-right font-mono">
                      {formatDelta(tx.delta, tx.transaction_type)}
                    </td>
                    <td className="py-3 text-sm text-center font-mono text-gray-300">
                      {typeof tx.balance_after === 'number' ? tx.balance_after.toFixed(2) : '—'}
                    </td>
                    <td className="py-3 text-sm text-gray-400 truncate max-w-[150px]" title={tx.metadata?.title || tx.correlation_id || '—'}>
                      {tx.metadata?.title || tx.book_title || (tx.correlation_id ? tx.correlation_id.slice(0, 20) + '...' : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-cyan-500/20 bg-gray-800/30 text-xs text-gray-500">
          Showing last {transactions.length} transactions • Balance is ledger-derived (authoritative)
        </div>
      </div>
    </div>
  );
}
